interface User {
	_id: string;
	department?: string;
	role?: string;
	profile_information?: {
		name?: string;
		surname?: string;
		department?: string;
	};
	user?: {
		_id: string;
		department?: string;
		profile_information?: {
			department?: string;
		};
	};
}

interface Position {
	x: number;
	y: number;
}

// Department color mapping
const getDepartmentColor = (departmentName: string) => {
	const colors: Record<string, string> = {
		'Executive': '#8b5cf6',
		'Engineering': '#06b6d4',
		'Sales': '#10b981',
		'Marketing': '#f59e0b',
		'Human Resources': '#ef4444',
		'Reception': '#6B7280',
		'General': '#8B5CF6',
		'Unassigned': '#9CA3AF',
	};
	return colors[departmentName] || '#6b7280';
};

// Department grouping utility
const groupUsersByDepartment = (users: User[]) => {
	const grouped = users.reduce(
		(acc, user) => {
			// Handle both nested user structure (from API) and direct user objects
			// API returns: { user: { profile_information: { department: 'X' } } }
			// Direct user: { department: 'X' } or { profile_information: { department: 'X' } }
			let dept = 'Unassigned';

			if (user.user) {
				// Nested structure from API (company.users or user.connectedUsers)
				// Priority: company.users.department > user.profile_information.department > user.department
				dept =
					user.department || // ← Company-level department (highest priority)
					user.user.profile_information?.department ||
					user.user.department ||
					'Unassigned';
			} else {
				// Direct user object
				dept =
					user.department || // ← Company-level department (highest priority)
					user.profile_information?.department ||
					'Unassigned';
			}

			// Ensure users without any department are marked as "Unassigned"
			if (!dept || dept === '' || dept === null || dept === undefined) {
				dept = 'Unassigned';
			}

			if (!acc[dept]) {
				acc[dept] = [];
			}

			// Push the actual user object (unwrap if nested)
			acc[dept].push(user.user || user);
			return acc;
		},
		{} as Record<string, User[]>
	);

	// Sort departments by priority (Executive first, then alphabetically, Unassigned last)
	const priority = [
		'Executive',
		'Engineering',
		'Sales',
		'Marketing',
		'Human Resources',
		'Reception',
	];
	const sortedDepartments = Object.keys(grouped).sort((a, b) => {
		// Unassigned always goes last
		if (a === 'Unassigned') return 1;
		if (b === 'Unassigned') return -1;

		const aIndex = priority.indexOf(a);
		const bIndex = priority.indexOf(b);
		if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
		if (aIndex !== -1) return -1;
		if (bIndex !== -1) return 1;
		return a.localeCompare(b);
	});

	return sortedDepartments.map((dept) => ({
		name: dept,
		users: grouped[dept],
		color: getDepartmentColor(dept),
		count: grouped[dept].length,
	}));
};

// Department positioning strategy for organized layout
export const calculateDepartmentPositions = (
	users: User[],
	viewportWidth: number = 4000,
	viewportHeight: number = 4000
): Record<string, Position> => {
	const positions: Record<string, Position> = {};

	// Group users by department
	const departmentGroups = groupUsersByDepartment(users);

	// Calculate positions for each department
	const departmentsPerRow = 3; // 3 departments per row
	const departmentSpacing = 800; // Space between department clusters
	const userSpacing = 250; // Space between users within a department

	departmentGroups.forEach((department, index) => {
		const row = Math.floor(index / departmentsPerRow);
		const col = index % departmentsPerRow;

		// Base position for this department cluster
		const baseX = 400 + col * departmentSpacing;
		const baseY = 300 + row * departmentSpacing;

		// Position users within this department
		department.users.forEach((user, userIndex) => {
			// Skip if user already has a position (like owner)
			if (positions[user._id]) return;

			// Arrange users in a grid within the department
			const usersPerRow = Math.ceil(Math.sqrt(department.users.length));
			const userRow = Math.floor(userIndex / usersPerRow);
			const userCol = userIndex % usersPerRow;

			const x = baseX + userCol * userSpacing;
			const y = baseY + userRow * userSpacing;

			positions[user._id] = { x, y };
		});
	});

	return positions;
};

// Enhanced positioning with owner at center and departments around
export const calculateOrganizedPositions = (
	users: User[],
	owner?: User,
	viewportWidth: number = 4000,
	viewportHeight: number = 4000
): Record<string, Position> => {
	const positions: Record<string, Position> = {};

	// Position owner at center
	if (owner) {
		positions[owner._id] = {
			x: viewportWidth / 2 - 100, // Center the card
			y: viewportHeight / 2 - 60,
		};
	}

	// Group remaining users by department
	const departmentGroups = groupUsersByDepartment(users);
	console.log('🎯 Department groups for positioning:', departmentGroups);

	// Position departments in a circle around the owner
	const centerX = viewportWidth / 2;
	const centerY = viewportHeight / 2;
	const radius = 800; // Increased radius for better spacing

	departmentGroups.forEach((department, index) => {
		const angle =
			(index / Math.max(departmentGroups.length, 1)) * 2 * Math.PI;
		const departmentX = centerX + Math.cos(angle) * radius;
		const departmentY = centerY + Math.sin(angle) * radius;

		console.log(
			`🎯 Positioning department ${department.name} at (${departmentX}, ${departmentY})`
		);

		// Position users within this department in a small cluster
		department.users.forEach((user, userIndex) => {
			// Skip owner (already positioned)
			if (user._id === owner?._id) return;

			// Create a small cluster around the department center
			const clusterRadius = 200; // Increased cluster radius
			const userAngle =
				(userIndex / Math.max(department.users.length, 1)) *
				2 *
				Math.PI;
			const userX = departmentX + Math.cos(userAngle) * clusterRadius;
			const userY = departmentY + Math.sin(userAngle) * clusterRadius;

			positions[user._id] = { x: userX, y: userY };
			console.log(
				`🎯 Positioning user ${user.profile_information?.name || user._id} at (${userX}, ${userY})`
			);
		});
	});

	console.log('🎯 Final positions calculated:', positions);
	return positions;
};

// Simple grid positioning for fallback
export const calculateGridPositions = (
	users: User[],
	viewportWidth: number = 4000,
	viewportHeight: number = 4000
): Record<string, Position> => {
	const positions: Record<string, Position> = {};

	const usersPerRow = Math.ceil(Math.sqrt(users.length));
	const spacing = 300;
	const startX = 400;
	const startY = 300;

	users.forEach((user, index) => {
		const row = Math.floor(index / usersPerRow);
		const col = index % usersPerRow;

		positions[user._id] = {
			x: startX + col * spacing,
			y: startY + row * spacing,
		};
	});

	return positions;
};

// Department hierarchy level mapping
// Level 0: Executive (top)
// Level 1: Management departments (Engineering, Sales, Marketing, HR, etc.)
// Level 2: Support departments (Reception, General, etc.)
// Level -1: Unassigned (special case - placed horizontally below Executive)
export const getDepartmentHierarchyLevel = (departmentName: string): number => {
	const hierarchy: Record<string, number> = {
		'Executive': 0,
		'Management': 1,
		'Engineering': 1,
		'Sales': 1,
		'Marketing': 1,
		'Human Resources': 1,
		'HR': 1, // Alternative name
		'Reception': 2,
		'General': 2,
		'Support': 2,
		'Unassigned': -1, // Special case - placed horizontally below Executive
	};
	return hierarchy[departmentName] ?? 2; // Default to level 2 for unknown departments
};

// Calculate bounding box for a department's users
interface BoundingBox {
	minX: number;
	maxX: number;
	minY: number;
	maxY: number;
	width: number;
	height: number;
}

const calculateDepartmentBoundingBox = (
	users: User[],
	userPositions: Record<string, Position>,
	userCardWidth: number = 200,
	userCardHeight: number = 120
): BoundingBox | null => {
	const positions = users
		.map(user => userPositions[user._id])
		.filter(pos => pos !== undefined);

	if (positions.length === 0) return null;

	const minX = Math.min(...positions.map(pos => pos.x));
	const maxX = Math.max(...positions.map(pos => pos.x + userCardWidth));
	const minY = Math.min(...positions.map(pos => pos.y));
	const maxY = Math.max(...positions.map(pos => pos.y + userCardHeight));

	return {
		minX,
		maxX,
		minY,
		maxY,
		width: maxX - minX,
		height: maxY - minY,
	};
};

// Check if two bounding boxes overlap
const boxesOverlap = (box1: BoundingBox, box2: BoundingBox, padding: number = 50): boolean => {
	return (
		box1.minX - padding < box2.maxX + padding &&
		box1.maxX + padding > box2.minX - padding &&
		box1.minY - padding < box2.maxY + padding &&
		box1.maxY + padding > box2.minY - padding
	);
};

// Hierarchical layout with non-overlapping bounding boxes
export const calculateHierarchicalPositions = (
	users: User[],
	owner?: User,
	viewportWidth: number = 4000,
	viewportHeight: number = 4000,
	customHierarchyLevels?: Record<string, number>
): Record<string, Position> => {
	const positions: Record<string, Position> = {};
	const userCardWidth = 200;
	const userCardHeight = 120;
	const verticalSpacing = 1200; // Space between hierarchy levels (significantly increased for better separation)
	const horizontalSpacing = 800; // Space between departments at same level (significantly increased for better separation)
	const padding = 150; // Padding around bounding boxes (increased to ensure no overlap)
	const userSpacing = 280; // Spacing between users within a department (increased from 250)
	const minDepartmentGap = 200; // Minimum gap between department bounding boxes

	// Group users by department
	const departmentGroups = groupUsersByDepartment(users);

	// Separate departments by hierarchy level
	const departmentsByLevel = new Map<number, typeof departmentGroups>();
	let unassignedDepartment: typeof departmentGroups[0] | null = null;

	departmentGroups.forEach(dept => {
		if (dept.name === 'Unassigned') {
			unassignedDepartment = dept;
		} else {
			// Use custom hierarchy level if provided, otherwise use default
			const level = customHierarchyLevels?.[dept.name] ?? getDepartmentHierarchyLevel(dept.name);
			if (!departmentsByLevel.has(level)) {
				departmentsByLevel.set(level, []);
			}
			departmentsByLevel.get(level)!.push(dept);
		}
	});

	// Store group information
	const groupInfo: Record<string, {
		department: typeof departmentGroups[0];
		bounds: BoundingBox;
		usersPerRow: number;
		groupWidth: number;
		groupHeight: number;
		position: { x: number; y: number };
	}> = {};

	// FIRST PASS: Calculate ALL group dimensions FIRST (before positioning)
	// This gives us the total size each group will occupy
	const allDepartmentsToPosition: Array<{ department: typeof departmentGroups[0]; level: number }> = [];
	
	// Add all departments with their levels
	departmentGroups.forEach(dept => {
		if (dept.name !== 'Unassigned') {
			const level = customHierarchyLevels?.[dept.name] ?? getDepartmentHierarchyLevel(dept.name);
			allDepartmentsToPosition.push({ department: dept, level });
		}
	});
	
	// Add Unassigned at the end
	if (unassignedDepartment) {
		allDepartmentsToPosition.push({ department: unassignedDepartment, level: -1 });
	}

	// Calculate dimensions for ALL groups first
	// Use accurate calculations that account for actual card dimensions
	allDepartmentsToPosition.forEach(({ department }) => {
		const usersPerRow = Math.ceil(Math.sqrt(department.users.length));
		const estimatedRows = Math.ceil(department.users.length / usersPerRow);
		
		// Calculate width: (columns - 1) * spacing + card width
		// This accounts for the fact that cards have width, not just spacing between them
		const actualColumns = Math.min(usersPerRow, department.users.length);
		const groupWidth = (actualColumns > 1) 
			? ((actualColumns - 1) * userSpacing) + userCardWidth
			: userCardWidth;
		
		// Calculate height: (rows - 1) * spacing + card height
		const groupHeight = (estimatedRows > 1)
			? ((estimatedRows - 1) * userSpacing) + userCardHeight
			: userCardHeight;
		
		// Store group info with dimensions (position will be set later)
		groupInfo[department.name] = {
			department,
			bounds: {
				minX: 0,
				maxX: groupWidth + (padding * 2),
				minY: 0,
				maxY: groupHeight + (padding * 2),
				width: groupWidth + (padding * 2),
				height: groupHeight + (padding * 2)
			},
			usersPerRow,
			groupWidth,
			groupHeight,
			position: { x: 0, y: 0 } // Will be set during positioning
		};
	});

	// SECOND PASS: Position groups sequentially, ensuring no overlap
	// Sort by hierarchy level (Executive first, then others, Unassigned last)
	allDepartmentsToPosition.sort((a, b) => {
		if (a.level === -1) return 1; // Unassigned goes last
		if (b.level === -1) return -1;
		return a.level - b.level;
	});

	const startX = viewportWidth / 2;
	let currentY = 500; // Start from top
	const positionedGroups: Array<{ name: string; bounds: BoundingBox; level: number }> = [];
	let currentLevel = -999; // Track current level
	let levelStartY = currentY;
	let levelStartX = startX;

	// Position each group sequentially, checking against all previously positioned groups
	allDepartmentsToPosition.forEach(({ department, level }) => {
		const info = groupInfo[department.name];
		if (!info) return;

		// Check if we're starting a new level
		if (level !== currentLevel) {
			currentLevel = level;
			// Find the maximum bottom Y of all groups at previous levels
			if (positionedGroups.length > 0) {
				let maxBottomY = 0;
				positionedGroups.forEach(g => {
					maxBottomY = Math.max(maxBottomY, g.bounds.maxY);
				});
				levelStartY = maxBottomY + verticalSpacing + minDepartmentGap;
				currentY = levelStartY;
			} else {
				levelStartY = currentY;
			}
			levelStartX = startX;
		}

		let groupX = levelStartX;
		let groupY = levelStartY;
		let foundPosition = false;
		let attempts = 0;
		const maxAttempts = 200;

		// Try to find a non-overlapping position
		while (!foundPosition && attempts < maxAttempts) {
			// Check if this position overlaps with any existing group
			const testBounds: BoundingBox = {
				minX: groupX,
				maxX: groupX + info.bounds.width,
				minY: groupY,
				maxY: groupY + info.bounds.height,
				width: info.bounds.width,
				height: info.bounds.height
			};

			let hasOverlap = false;
			let maxOverlapX = groupX;
			for (const existingGroup of positionedGroups) {
				if (boxesOverlap(testBounds, existingGroup.bounds, minDepartmentGap + padding)) {
					hasOverlap = true;
					// Move to the right of this overlapping group
					maxOverlapX = Math.max(maxOverlapX, existingGroup.bounds.maxX + horizontalSpacing + minDepartmentGap);
				}
			}

			if (!hasOverlap) {
				foundPosition = true;
			} else {
				// Move to the right of the rightmost overlapping group
				groupX = maxOverlapX;
				attempts++;
				
				// If we've tried many times horizontally, try moving down
				if (attempts > 100) {
					groupY += verticalSpacing + minDepartmentGap;
					groupX = levelStartX; // Reset X to level start
					attempts = 0;
				}
			}
		}

		// Update levelStartX for next group at this level
		levelStartX = groupX + info.bounds.width + horizontalSpacing + minDepartmentGap;

		// Set the group position
		info.position = { x: groupX, y: groupY };
		
		// Position users within this group
		department.users.forEach((user, userIndex) => {
			if (user._id === owner?._id) return;

			const userRow = Math.floor(userIndex / info.usersPerRow);
			const userCol = userIndex % info.usersPerRow;

			positions[user._id] = {
				x: groupX + padding + (userCol * userSpacing),
				y: groupY + padding + (userRow * userSpacing),
			};
		});

		// Calculate ACTUAL bounds from positioned users
		const deptPositions: Record<string, Position> = {};
		department.users.forEach(user => {
			if (positions[user._id] && user._id !== owner?._id) {
				deptPositions[user._id] = positions[user._id];
			}
		});
		
		const actualBounds = calculateDepartmentBoundingBox(
			department.users.filter(u => u._id !== owner?._id),
			deptPositions,
			userCardWidth,
			userCardHeight
		);
		
		// Update bounds with actual position and dimensions
		if (actualBounds) {
			info.bounds = {
				minX: actualBounds.minX - padding,
				maxX: actualBounds.maxX + padding,
				minY: actualBounds.minY - padding,
				maxY: actualBounds.maxY + padding,
				width: (actualBounds.maxX + padding) - (actualBounds.minX - padding),
				height: (actualBounds.maxY + padding) - (actualBounds.minY - padding)
			};
		} else {
			info.bounds = {
				minX: groupX,
				maxX: groupX + info.bounds.width,
				minY: groupY,
				maxY: groupY + info.bounds.height,
				width: info.bounds.width,
				height: info.bounds.height
			};
		}

		// CRITICAL: Check if actual bounds overlap with existing groups and adjust if needed
		// This ensures department bounding boxes don't overlap, even if estimated bounds were wrong
		let needsAdjustment = true;
		let adjustmentAttempts = 0;
		const maxAdjustmentAttempts = 50;
		
		while (needsAdjustment && adjustmentAttempts < maxAdjustmentAttempts) {
			needsAdjustment = false;
			
			// Check against all previously positioned groups
			for (const existingGroup of positionedGroups) {
				if (boxesOverlap(info.bounds, existingGroup.bounds, minDepartmentGap + padding)) {
					needsAdjustment = true;
					
					// Calculate required translation
					const verticalOverlap = Math.min(info.bounds.maxY, existingGroup.bounds.maxY) - Math.max(info.bounds.minY, existingGroup.bounds.minY);
					const horizontalOverlap = Math.min(info.bounds.maxX, existingGroup.bounds.maxX) - Math.max(info.bounds.minX, existingGroup.bounds.minX);
					
					// Determine translation direction (prioritize vertical for different levels)
					if (verticalOverlap > horizontalOverlap || level !== existingGroup.level) {
						// Move down
						const translationY = existingGroup.bounds.maxY + verticalSpacing + minDepartmentGap - info.bounds.minY;
						info.position.y += translationY;
						info.bounds.minY += translationY;
						info.bounds.maxY += translationY;
						
						// Move all users down
						department.users.forEach(user => {
							if (positions[user._id] && user._id !== owner?._id) {
								positions[user._id].y += translationY;
							}
						});
					} else {
						// Move right
						const translationX = existingGroup.bounds.maxX + horizontalSpacing + minDepartmentGap - info.bounds.minX;
						info.position.x += translationX;
						info.bounds.minX += translationX;
						info.bounds.maxX += translationX;
						
						// Move all users right
						department.users.forEach(user => {
							if (positions[user._id] && user._id !== owner?._id) {
								positions[user._id].x += translationX;
							}
						});
					}
					
					adjustmentAttempts++;
					break; // Re-check from the beginning after adjustment
				}
			}
		}

		// Add to positioned groups with FINAL bounds (after any adjustments)
		positionedGroups.push({ name: department.name, bounds: info.bounds, level });

		// Update currentY for next group (move down if needed)
		currentY = Math.max(currentY, info.bounds.maxY);
	});

	// Unassigned is now positioned in the main loop above

	// Position owner at center of Executive level (or top if no Executive)
	if (owner) {
		const executiveY = sortedLevels.includes(0) ? 500 : 500;
		positions[owner._id] = {
			x: startX - (userCardWidth / 2),
			y: executiveY - 100, // Slightly above Executive level
		};
	}

	// Now check for overlaps using group bounds and adjust positions
	// Use the group info we already calculated - these are the actual group sizes
	const departmentBoxes: Record<string, BoundingBox> = {};
	Object.keys(groupInfo).forEach(name => {
		departmentBoxes[name] = groupInfo[name].bounds;
	});

	// Resolve overlaps by shifting departments and ensure minimum gaps
	// Sort departments by Y position (then X) to process from top to bottom, left to right
	const sortedDepartmentNames = Object.keys(departmentBoxes).sort((a, b) => {
		const boxA = departmentBoxes[a];
		const boxB = departmentBoxes[b];
		if (!boxA || !boxB) return 0;
		// First sort by Y, then by X
		if (Math.abs(boxA.minY - boxB.minY) > 50) {
			return boxA.minY - boxB.minY;
		}
		return boxA.minX - boxB.minX;
	});

	// Iterate through departments and resolve overlaps with minimum gap enforcement
	// Use multiple passes to handle cascading overlaps
	for (let pass = 0; pass < 10; pass++) {
		let hasOverlaps = false;
		
		for (let i = 0; i < sortedDepartmentNames.length; i++) {
			const dept1 = sortedDepartmentNames[i];
			const box1 = departmentBoxes[dept1];
			if (!box1) continue;

			for (let j = i + 1; j < sortedDepartmentNames.length; j++) {
				const dept2 = sortedDepartmentNames[j];
				const box2 = departmentBoxes[dept2];
				if (!box2) continue;

				// Check if boxes overlap or are too close (within minimum gap)
				// More aggressive check: any proximity in either direction requires spacing
				const verticalOverlap = box1.maxY > box2.minY && box1.minY < box2.maxY;
				const horizontalOverlap = box1.maxX > box2.minX && box1.minX < box2.maxX;
				const verticalProximity = (box1.maxY + minDepartmentGap + padding >= box2.minY && box1.minY <= box2.maxY + minDepartmentGap + padding) ||
					(box2.maxY + minDepartmentGap + padding >= box1.minY && box2.minY <= box1.maxY + minDepartmentGap + padding);
				const horizontalProximity = (box1.maxX + minDepartmentGap + padding >= box2.minX && box1.minX <= box2.maxX + minDepartmentGap + padding) ||
					(box2.maxX + minDepartmentGap + padding >= box1.minX && box2.minX <= box1.maxX + minDepartmentGap + padding);
				
				// Need spacing if there's actual overlap OR if groups are too close in both dimensions
				const needsSpacing = boxesOverlap(box1, box2, padding) || 
					(verticalProximity && horizontalProximity);

				if (needsSpacing) {
					hasOverlaps = true;
					const dept2Users = departmentGroups.find(d => d.name === dept2)?.users || [];
					
					// Calculate actual overlap and required spacing
					const verticalOverlap = Math.min(box1.maxY, box2.maxY) - Math.max(box1.minY, box2.minY);
					const horizontalOverlap = Math.min(box1.maxX, box2.maxX) - Math.max(box1.minX, box2.minX);
					
					// Calculate required translation to achieve minimum gap
					// For vertical: ensure box2 starts at least minDepartmentGap + padding below box1
					const requiredVerticalTranslation = box1.maxY + minDepartmentGap + padding - box2.minY;
					// For horizontal: ensure box2 starts at least minDepartmentGap + padding to the right of box1
					const requiredHorizontalTranslation = box1.maxX + minDepartmentGap + padding - box2.minX;
					
					// Get current group info
					const groupInfo2 = groupInfo[dept2];
					if (!groupInfo2) return; // Safety check
					
					// Calculate translations needed
					const translationY = Math.max(0, requiredVerticalTranslation);
					const translationX = Math.max(0, requiredHorizontalTranslation);
					
					// Prioritize vertical spacing (especially important for Unassigned below other departments)
					// If there's vertical proximity/overlap, translate down
					if (verticalProximity && translationY > 0) {
						// Update group position
						groupInfo2.position.y += translationY;
						
						// Translate all users in the group by the same amount
						dept2Users.forEach(user => {
							if (positions[user._id] && user._id !== owner?._id) {
								positions[user._id].y += translationY;
							}
						});
						
						// Update group bounds
						groupInfo2.bounds.minY += translationY;
						groupInfo2.bounds.maxY += translationY;
						departmentBoxes[dept2] = groupInfo2.bounds;
						box2 = groupInfo2.bounds; // Update box2 for potential horizontal translation
					}
					
					// Also handle horizontal spacing if needed (can happen in addition to vertical)
					if (horizontalProximity && translationX > 0) {
						// Update group position
						groupInfo2.position.x += translationX;
						
						// Translate all users in the group by the same amount
						dept2Users.forEach(user => {
							if (positions[user._id] && user._id !== owner?._id) {
								positions[user._id].x += translationX;
							}
						});
						
						// Update group bounds
						groupInfo2.bounds.minX += translationX;
						groupInfo2.bounds.maxX += translationX;
						departmentBoxes[dept2] = groupInfo2.bounds;
					}
				}
			}
		}
		
		// If no overlaps found, we're done
		if (!hasOverlaps) break;
	}

	return positions;
};
